import type { CSSProperties, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * Mindtrip-style feature card with floating "stickers" (DESIGN.md, section 6).
 * A sticker is a small HTML card, not an image: thumbnail + two lines of real
 * data, tilted, overhanging the card's bottom edge.
 */

export type StickerTone = "good" | "alert";

export interface StickerProps {
  /** Thumbnail URL. Decorative: the text carries the fact. */
  img: string;
  /** Place, in small label weight ("Islas del Rosario"). */
  label: string;
  /** The fact, in title weight ("Seguro obligatorio"). Must come from the data. */
  fact: string;
  tone?: StickerTone;
  /** Fixed tilt in degrees, between -4 and 3. */
  tilt?: number;
}

export function Sticker({ img, label, fact, tone, tilt = 0 }: StickerProps) {
  return (
    <div className="pq-sticker" style={{ "--tilt": `${tilt}deg` } as CSSProperties}>
      <img src={img} alt="" width={64} height={64} loading="lazy" decoding="async" />
      <span>
        <span className="pq-sticker-label">{label}</span>
        <span className={`pq-sticker-fact${tone ? ` pq-sticker-${tone}` : ""}`}>{fact}</span>
      </span>
    </div>
  );
}

export function FeatureCard({
  icon: Icon,
  title,
  children,
  stickers,
  link,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  stickers: StickerProps[];
  /** Optional text link under the body (e.g. to the section with the detail). */
  link?: { href: string; label: string };
}) {
  return (
    <article className="pq-feature">
      <Icon size={24} strokeWidth={1.75} aria-hidden="true" />
      <h3 className="pq-feature-title">{title}</h3>
      <p className="pq-feature-text">{children}</p>
      {link ? (
        <a href={link.href} className="pq-link pq-feature-link">
          {link.label}
          <span aria-hidden="true"> →</span>
        </a>
      ) : null}
      <div className="pq-stickers">
        {stickers.map((s) => (
          <Sticker key={`${s.label}-${s.fact}`} {...s} />
        ))}
      </div>
    </article>
  );
}
